import i18n from "@sg/i18n";
import { lazy } from "react";
import { Navigate, Params, PathMatch, RouteObject } from "react-router-dom";
import { Layout } from "~/components";
import { PathBreadcrumb, QueryBreadcrumb } from "~/components/Layout/Breadcrumbs";
import {
  CounterpartyResourceType,
  OrgResourceType,
  UserResourceType,
  useCounterparty,
  useMe,
  useOrg,
  useUser,
} from "~/hooks/query";
import { RoleV2 } from "~/types";

const AssetMasterPage = lazy(() => import("~/pages/AssetMaster"));
const AuditPage = lazy(() => import("~/pages/Audit"));
const AuthErrorPage = lazy(() => import("~/pages/AuthError"));
const CounterpartyAgreementsPage = lazy(() => import("~/pages/Counterparty/CounterpartyAgreementsPage"));
const CounterpartyTransactionsPage = lazy(() => import("~/pages/Counterparty/CounterpartyTransactionsPage"));
const InternalPositionsPage = lazy(() => import("~/pages/InternalPositions"));
const LoginPage = lazy(() => import("~/pages/Login"));
const MarginPage = lazy(() => import("~/pages/Margin"));
const OnboardingPage = lazy(() => import("~/pages/Onboarding"));
const OnboardingReviewPage = lazy(() => import("~/pages/OnboardingReview"));
const SuperAdminOnboardingReviewDatagrid = lazy(() => import("~/pages/OnboardingReview/SuperAdminOnboardingReviewDatagrid"));
const OrdersPage = lazy(() => import("~/pages/Orders"));
const OrganizationPage = lazy(() => import("~/pages/Organization"));
const OrganizationDetailsEntitiesPage = lazy(() => import("~/pages/OrganizationDetails/OrganizationDetailsEntities"));
const OrganizationDetailsUsersPage = lazy(() => import("~/pages/OrganizationDetails/OrganizationDetailsUsers"));
const ProfilePage = lazy(() => import("~/pages/Profile"));
const PositionsPage = lazy(() => import("~/pages/Positions"));
const RolePage = lazy(() => import("~/pages/Role"));
const SwapBookingPage = lazy(() => import("~/pages/SwapBooking"));
const ReconciliationPage = lazy(() => import("~/pages/Reconciliation"));
const VenueBookPage = lazy(() => import("~/pages/VenueBook"));
const TradePage = lazy(() => import("~/pages/Trade"));
const TransfersPage = lazy(() => import("~/pages/Transfers"));
const UserPage = lazy(() => import("~/pages/User"));
const UserDetailsPage = lazy(() => import("~/pages/UserDetails"));

enum ROUTE {
  ASSET_MASTER = "/asset-master",
  AUDIT = "/audit",
  AUTH_ERROR = "/auth-error",
  COLLATERAL = "/orgs/:orgId/entities/:cpId/collateral",
  COUNTERPARTY = "/orgs/:orgId/entities/:cpId",
  COUNTERPARTY_DETAILS_AGREEMENTS = "/orgs/:orgId/entities/:cpId/agreements",
  COUNTERPARTY_DETAILS_CONFIRMS = "/orgs/:orgId/entities/:cpId/confirms",
  FORWARDS = "/orgs/:orgId/entities/:cpId/forwards",
  PERPETUAL_FORWARDS = "/orgs/:orgId/entities/:cpId/perpetual-forwards",
  TRANSFERS = "/orgs/:orgId/entities/:cpId/transfers",
  HOME = "/",
  INTERNAL_POSITIONS = "/positions",
  LOGIN = "/login",
  ONBOARDING = "/orgs/:orgId/entities/:cpId/onboarding/:onboardingId",
  ONBOARDING_REVIEW = "/onboarding/review",
  ORDERS = "/orders",
  ORGANIZATIONS = "/orgs",
  ORGANIZATION_DETAILS = "/orgs/:orgId",
  ORGANIZATION_DETAILS_USERS = "/orgs/:orgId/users",
  ORGANIZATION_DETAILS_COUNTERPARTIES = "/orgs/:orgId/entities",
  ORGANIZATION_DETAILS_POSITIONS = "/orgs/:orgId/entities/:cpId/positions",
  ORGANIZATION_ONBOARDING_REVIEW = "/orgs/:orgId/onboarding",
  PROFILE = "/profile",
  ROLES = "/roles",
  OPTIONS = "/orgs/:orgId/entities/:cpId/options",
  SPREADS = "/orgs/:orgId/entities/:cpId/spreads",
  SWAP_BOOKING = "/swap-booking",
  VENUE_BOOK = "/venue-book",
  RECONCILIATION = "/reconciliation",
  USERS = "/users",
  USER_DETAILS = "/orgs/:orgId/users/:userId",
}

type RouteConfigurationProps = {
  getId: (match: PathMatch) => string | undefined;
  getLabel: (data: never) => string | undefined;
  onSwitcherChange?: (id: string) => { route: string; params: Params<string> };
  query: unknown;
  field: string;
  disabled: boolean;
  hideCrumb: boolean;
  hideLayout: boolean;
  id: ROUTE;
  title: string;
  roles: RoleV2[];
};

type RouteConfiguration = RouteObject & {
  breadcrumb?: unknown;
  children?: RouteConfiguration[];
  props?: Partial<RouteConfigurationProps>;
};

const optionsProps= {
  id: ROUTE.OPTIONS,
  title: i18n.t("options"),
  roles:  [RoleV2.TRADER]
};


const ROUTE_CONFIG: RouteConfiguration[] = [
  {
    path: "/",
    element: <Layout />,
    children: [

      {
        index: true,
        element: <TradePage />,
        props: {
          id: ROUTE.PERPETUAL_FORWARDS,
          title: i18n.t("perpetual_forwards"),
          roles: [],
        },
      },
      {
        path: ROUTE.ORGANIZATION_DETAILS,
        element: <OrganizationDetailsEntitiesPage />,
        props: {
          id: ROUTE.HOME,
          title: i18n.t("org"),
          roles: [],
        },
      },
      {
        path: "audit",
        element: <AuditPage />,
        props: {
          id: ROUTE.AUDIT,
          title: i18n.t("audit"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "auth-error",
        element: <AuthErrorPage />,
        props: {
          hideLayout: true,
          id: ROUTE.AUTH_ERROR,
        },
      },
      {
        path: "asset-master",
        element: <AssetMasterPage />,
        props: {
          id: ROUTE.ASSET_MASTER,
          title: i18n.t("asset_master"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "swap-booking",
        element: <SwapBookingPage />,
        props: {
          id: ROUTE.SWAP_BOOKING,
          title: i18n.t("swap_booking"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "venue-book",
        element: <VenueBookPage />,
        props: {
          id: ROUTE.VENUE_BOOK,
          title: i18n.t("venue_book"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "reconciliation",
        element: <ReconciliationPage />,
        props: {
          id: ROUTE.RECONCILIATION,
          title: i18n.t("reconciliation"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "login",
        element: <LoginPage />,
        props: {
          hideLayout: true,
          id: ROUTE.LOGIN,
        },
      },

      {
        path: "onboarding",
        children: [
          {
            path: "review",
            element: <SuperAdminOnboardingReviewDatagrid />,
            props: {
              id: ROUTE.ONBOARDING_REVIEW,
              title: i18n.t("onboarding"),
              roles: [RoleV2.SUPER_ADMIN],
            },
          },
        ],
      },
      {
        path: "orgs",
        children: [
          {
            index: true,
            element: <OrganizationPage />,
            props: {
              hideCrumb: true,
              id: ROUTE.ORGANIZATIONS,
              roles: [RoleV2.SUPER_ADMIN],
              title: i18n.t("orgs"),
            },
          },
          {
            path: ":orgId",
            children: [
              {
                index: true,
                element: <Navigate to="./entities" replace />,
                breadcrumb: QueryBreadcrumb<OrgResourceType>,
                props: {
                  getId: (match: PathMatch) => match.params.orgId,
                  getLabel: (data: OrgResourceType) => data.display_name,
                  id: ROUTE.ORGANIZATION_DETAILS,
                  query: useOrg,
                  roles: [],
                },
              },
              {
                path: "onboarding",
                props: {
                  id: ROUTE.ORGANIZATION_ONBOARDING_REVIEW,
                },
                children: [
                  {
                    index: true,
                    element: <OnboardingReviewPage />,
                    props: {
                      roles: [RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                    },
                  },
                ],
              },
              {
                path: "entities",
                props: {
                  id: ROUTE.ORGANIZATION_DETAILS_COUNTERPARTIES,
                },
                children: [
                  {
                    index: true,
                    element: <OrganizationDetailsEntitiesPage />,
                    props: {
                      roles: [RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                    },
                  },
                  {
                    path: ":cpId",
                    children: [
                      {
                        index: true,
                        element: <Navigate to="./perpetual-forwards" replace />,
                        breadcrumb: QueryBreadcrumb<CounterpartyResourceType>,
                        props: {
                          getId: (match: PathMatch) => match.params.cpId,
                          getLabel: (data: CounterpartyResourceType) => data.name,
                          query: useCounterparty,
                          id: ROUTE.PERPETUAL_FORWARDS,
                          roles: optionsProps.roles,
                        },
                      },
                      {
                        path: "positions",
                        props: {
                          id: ROUTE.ORGANIZATION_DETAILS_POSITIONS,
                        },
                        children: [
                          {
                            index: true,
                            element: <PositionsPage />,
                            props: {
                              roles: [RoleV2.TRADER, RoleV2.FINOPS, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                            },
                          },
                        ],
                      },
                      {
                        path: "onboarding",
                        children: [
                          {
                            path: ":onboardingId",
                            element: <OnboardingPage />,
                            breadcrumb: PathBreadcrumb,
                            props: {
                              field: "onboardingId",
                              id: ROUTE.ONBOARDING,
                              roles: [RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING, RoleV2.ONBOARDING],
                            },
                          },
                        ],
                      },
                      {
                        path: "agreements",
                        props: {
                          id: ROUTE.COUNTERPARTY_DETAILS_AGREEMENTS,
                        },
                        children: [
                          {
                            index: true,
                            element: <CounterpartyAgreementsPage />,
                            props: {
                              roles: [RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                            },
                          },
                        ],
                      },
                      {
                        path: "options",
                        element: <TradePage />,
                        props: optionsProps
                      },
                      {
                        path: "spreads",
                        element: <TradePage />,
                        props: {
                          id: ROUTE.SPREADS,
                          title: i18n.t("spreads"),
                          roles: [RoleV2.TRADER]
                        }
                      },
                      {
                        path: "forwards",
                        element: <TradePage />,
                        props: {
                          id: ROUTE.FORWARDS,
                          title: i18n.t("forwards"),
                          roles: [RoleV2.TRADER]
                        }
                      },
                      {
                        path: "perpetual-forwards",
                        element: <TradePage key="perps" />,
                        props: {
                          id: ROUTE.PERPETUAL_FORWARDS,
                          title: i18n.t("perpetual_forwards"),
                          roles: [RoleV2.TRADER]
                        }
                      },
                      {
                        path: "collateral",
                        props: {
                          id: ROUTE.COLLATERAL,
                          title: i18n.t("collateral"),
                        },
                        children: [
                          {
                            index: true,
                            element: <MarginPage />,
                            props: {
                              roles: [RoleV2.FINOPS, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                            },
                          },
                        ],
                      },
                      {
                        path: "transfers",
                        props: {
                          id: ROUTE.TRANSFERS,
                          title: i18n.t("transfers"),
                        },
                        children: [
                          {
                            index: true,
                            element: <TransfersPage />,
                            props: {
                              roles: [RoleV2.FINOPS, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                            },
                          },
                        ],
                      },
                      {
                        path: "confirms",
                        props: {
                          id: ROUTE.COUNTERPARTY_DETAILS_CONFIRMS,
                        },
                        children: [
                          {
                            index: true,
                            element: <CounterpartyTransactionsPage />,
                            props: {
                              roles: [RoleV2.FINOPS, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "users",
                props: {
                  id: ROUTE.ORGANIZATION_DETAILS_USERS,
                },
                children: [
                  {
                    index: true,
                    element: <OrganizationDetailsUsersPage />,
                    props: {
                      roles: [RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                    },
                  },
                  {
                    path: ":userId",
                    element: <UserDetailsPage />,
                    breadcrumb: QueryBreadcrumb<UserResourceType>,
                    props: {
                      getId: (match: PathMatch) => match.params.userId,
                      getLabel: (data: UserResourceType) => data.email,
                      id: ROUTE.USER_DETAILS,
                      query: useUser,
                      roles: [RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING],
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "orders",
        element: <OrdersPage />,
        props: {
          id: ROUTE.ORDERS,
          title: i18n.t("orders"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "profile",
        element: <ProfilePage />,
        breadcrumb: QueryBreadcrumb<UserResourceType>,
        props: {
          id: ROUTE.PROFILE,
          query: useMe,
          getLabel: (data: UserResourceType) => data.email,
          roles: [],
        },
      },
      {
        path: "roles",
        element: <RolePage />,
        props: {
          id: ROUTE.ROLES,
          title: i18n.t("roles"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "positions",
        element: <InternalPositionsPage />,
        props: {
          id: ROUTE.INTERNAL_POSITIONS,
          title: i18n.t("positions"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "users",
        element: <UserPage />,
        props: {
          id: ROUTE.USERS,
          title: i18n.t("users"),
          roles: [RoleV2.SUPER_ADMIN],
        },
      },
      {
        path: "*",
        element: <div>{`${i18n.t("page_not_found")}`}</div>,
      },
    ],
  },
];

// Used to generate breadcrumb labels for the specified routes.
const BREADCRUMB_ROUTES: Omit<RouteConfiguration, "element">[] = [
  {
    path: "/orgs/:orgId/:cpId/onboarding",
    breadcrumb: i18n.t("onboarding:application"),
  },
  {
    path: "/asset-master",
    breadcrumb: i18n.t("asset_master"),
  },
];

export { BREADCRUMB_ROUTES, ROUTE, ROUTE_CONFIG };
export type { RouteConfiguration };

